import { CircularProgress, Stack, Typography } from '@mui/material';
import { useMappableFGASchema } from 'api/fga';
import {
	useSSOConfigCurrentFieldsInitialized,
	useSSOConfigMappings
} from 'contexts/SSOConfigFieldsContext';
import React, { useCallback, useMemo } from 'react';
import { IFGAGroupMapping } from 'types/api';
import { applySectionMappings, getMappingSections } from 'utils/fgaMapping';
import MDGapWrapper from '../MDRenderer/utilComponents/MDGapWrapper';
import FGAMappingSection from './FGAMappingSection';

const FGAMapping: React.FC = () => {
	const { getFGAMappings, setFGAMappings } = useSSOConfigMappings();
	const fieldsInitialized = useSSOConfigCurrentFieldsInitialized();
	const { data: model, isLoading, isError } = useMappableFGASchema();

	const sections = useMemo(
		() => (model ? getMappingSections(model, getFGAMappings() ?? []) : []),
		[model, getFGAMappings]
	);

	const handleSectionSubmit = useCallback(
		(fgaType: string, sectionMappings: IFGAGroupMapping[]) => {
			/* istanbul ignore if: sections only render once the model resolved */
			if (!model) {
				return;
			}
			setFGAMappings(
				applySectionMappings(
					model,
					getFGAMappings() ?? [],
					fgaType,
					sectionMappings
				)
			);
		},
		[model, getFGAMappings, setFGAMappings]
	);

	if (isLoading) {
		return (
			<MDGapWrapper>
				<Stack justifyContent="center" alignItems="center" height={200}>
					<CircularProgress />
				</Stack>
			</MDGapWrapper>
		);
	}

	// Returning null here too would make a failed request look like a project with no FGA
	// configured, and the admin would have no way to tell the two apart.
	if (isError) {
		return (
			<MDGapWrapper>
				<Stack mt={3}>
					<Typography variant="h6">
						{intl.tr('ssoConfig.fields.fgaMapping.header')}
					</Typography>
					<Typography variant="body1" color="error">
						{intl.tr('ssoConfig.fields.fgaMapping.loadError')}
					</Typography>
				</Stack>
			</MDGapWrapper>
		);
	}

	if (!model?.hasSchema || !sections.length) {
		return null;
	}

	return (
		<MDGapWrapper>
			<Stack>
				<Typography variant="h6">
					{intl.tr('ssoConfig.fields.fgaMapping.header')}
				</Typography>
				<Typography variant="body1">
					{intl.tr('ssoConfig.fields.fgaMapping.subHeader')}
				</Typography>
			</Stack>
			{sections.map((section) => (
				<FGAMappingSection
					key={section.fgaType}
					fgaType={section.fgaType}
					relationOptions={section.relationOptions}
					resourceOptions={section.resourceOptions}
					initialSectionMappings={fieldsInitialized ? section.mappings : null}
					onSubmitSection={handleSectionSubmit}
				/>
			))}
		</MDGapWrapper>
	);
};

export default FGAMapping;
